import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import PeopleIcon from "@mui/icons-material/People";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import TuneIcon from "@mui/icons-material/Tune";
import api from "../../axiosConfig";
import { getLeadStatusOptions } from "../../utils/leadStatusOptions";
import { whatsappAudienceApi } from "./api/whatsappAudienceApi";
import {
  AUDIENCE_PRESETS,
  buildAudienceNarrative,
  buildAudiencePlan,
  buildAudienceStatusBadges,
  getAudiencePreset,
  isManualAudience,
  normalizePositiveInteger,
  TARGET_AUDIENCE_OPTIONS,
  targetAudienceLabel,
  toAudienceRow,
} from "./audienceUtils";

const PRESET_ICONS = {
  all_eligible:    <PeopleIcon fontSize="small" />,
  never_contacted: <NewReleasesIcon fontSize="small" />,
  hot_leads:       <LocalFireDepartmentIcon fontSize="small" />,
  reengage_30:     <HistoryIcon fontSize="small" />,
  custom_audience: <TuneIcon fontSize="small" />,
  my_contacts:     <ContactPhoneIcon fontSize="small" />,
  ai_intelligence: <AutoAwesomeIcon fontSize="small" />,
};

function toBudgetInputValue(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
}

function parseBudgetInputValue(value, fallback = null) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return fallback;
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : Number.NaN;
}

export default function AudienceBuilder(props) {
  const {
    audienceMode,
    onAudienceModeChange,
    audienceChoice,
    onAudienceChoiceChange,
    filters,
    onFiltersChange,
    loading,
    setLoading,
    excludeRecent,
    setExcludeRecent,
    recentDays,
    setRecentDays,
    audienceSummary,
    previewRows,
    onAudiencePrepared,
    onUseAudience,
    whatsAppBudget,
    budgetLoading,
    canManageBudget = false,
    onWhatsAppBudgetUpdated,
    // Optional: pre-select a specific intelligence audience on mount
    intelligenceAudienceId,
  } = props;

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [approvedAudiences, setApprovedAudiences] = useState([]);
  const [selectedIntelligenceId, setSelectedIntelligenceId] = useState(intelligenceAudienceId ?? "");
  const [budgetEditorOpen, setBudgetEditorOpen] = useState(false);
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetError, setBudgetError] = useState("");
  const [budgetNotice, setBudgetNotice] = useState("");
  const [budgetForm, setBudgetForm] = useState({
    daily_limit: "",
    monthly_limit: "",
    reserve_buffer: "",
  });

  const preset = getAudiencePreset(audienceChoice);
  const manualSelection = isManualAudience(audienceChoice);
  const audienceBadges = buildAudienceStatusBadges(audienceSummary);
  const sampleSize = normalizePositiveInteger(filters.limit) || 200;
  const preferredLimit = normalizePositiveInteger(filters.preferred_limit);
  const sampleRows = previewRows?.slice(0, 20) || [];
  const budgetRemaining =
    whatsAppBudget?.remaining === null || whatsAppBudget?.remaining === undefined
      ? null
      : Number(whatsAppBudget.remaining);
  const budgetReserve = Number(whatsAppBudget?.reserve_buffer || 0);
  const budgetCostPerRecipient = Math.max(0.0001, Number(whatsAppBudget?.cost_per_recipient || 1));
  const budgetSendable =
    Number.isFinite(budgetRemaining)
      ? Math.max(0, Math.floor((budgetRemaining - budgetReserve) / budgetCostPerRecipient))
      : null;
  const audienceNarrative = useMemo(
    () => buildAudienceNarrative({ audienceKey: audienceChoice, filters, summary: audienceSummary }),
    [audienceChoice, filters, audienceSummary]
  );
  const canUseAudience = useMemo(() => {
    if (!audienceSummary) {
      return false;
    }

    if (manualSelection) {
      return Number(audienceSummary.totalFound || 0) > 0;
    }

    return Number(audienceSummary.eligibleFound || 0) > 0;
  }, [audienceSummary, manualSelection]);

  const clearPreparedAudience = () => {
    setRequestError("");
    onAudiencePrepared?.(null);
  };

  useEffect(() => {
    setBudgetForm({
      daily_limit: toBudgetInputValue(whatsAppBudget?.settings?.daily_limit ?? whatsAppBudget?.daily_limit),
      monthly_limit: toBudgetInputValue(whatsAppBudget?.settings?.monthly_limit ?? whatsAppBudget?.monthly_limit),
      reserve_buffer: toBudgetInputValue(whatsAppBudget?.settings?.reserve_buffer ?? whatsAppBudget?.reserve_buffer ?? 0),
    });
  }, [whatsAppBudget]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const [leadStatusRows, groupResponse] = await Promise.all([
          getLeadStatusOptions(),
          api.get("/marketing/contact-groups"),
        ]);

        if (!active) {
          return;
        }

        setStatusOptions(Array.isArray(leadStatusRows) ? leadStatusRows : []);
        const groups = groupResponse?.data?.data ?? groupResponse?.data ?? [];
        setContactGroups(Array.isArray(groups) ? groups : []);
      } catch {
        if (!active) {
          return;
        }

        setStatusOptions([]);
        setContactGroups([]);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // Fetch approved intelligence audiences when AI preset is active
  useEffect(() => {
    if (audienceChoice !== "ai_intelligence") return;
    let active = true;
    api.get("/intelligence/audiences/approved")
      .then((res) => { if (active) setApprovedAudiences(res.data?.audiences ?? []); })
      .catch(() => { if (active) setApprovedAudiences([]); });
    return () => { active = false; };
  }, [audienceChoice]);

  // Auto-apply segment filters when an intelligence audience is selected
  const applyIntelligenceAudience = (audienceId, audiences) => {
    const list = audiences ?? approvedAudiences;
    const chosen = list.find((a) => String(a.id) === String(audienceId));
    if (!chosen) return;
    const sf = chosen.segment_filters_json ?? {};
    onFiltersChange({
      ...filters,
      q: sf.q ?? "",
      source: sf.source ?? "",
      status: sf.status ?? "",
      contact_group_id: sf.contact_group_id ?? "",
      segment: sf.segment ?? "",
    });
  };

  // On mount: if intelligenceAudienceId was passed, jump straight to AI preset
  useEffect(() => {
    if (!intelligenceAudienceId) return;
    handlePresetSelect("ai_intelligence");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetSelect = (nextKey) => {
    const nextPreset = getAudiencePreset(nextKey);

    clearPreparedAudience();
    onAudienceChoiceChange(nextKey);
    onAudienceModeChange(nextKey === "custom_audience" ? "advanced" : "simple");
    onFiltersChange({
      ...filters,
      q: nextKey === "custom_audience" ? filters.q : "",
      source: nextKey === "custom_audience" ? filters.source : "",
      status: nextKey === "custom_audience" ? filters.status : "",
      contact_group_id: nextKey === "custom_audience" ? filters.contact_group_id : "",
      segment: nextPreset.segment || "",
      limit: nextKey === "custom_audience" ? filters.limit || 200 : filters.limit || 200,
      preferred_limit: filters.preferred_limit || "",
    });
    setExcludeRecent(nextPreset.defaultExcludeRecent);
    setRecentDays(nextPreset.defaultDays);
    setAdvancedOpen(nextKey === "custom_audience");
  };

  const handleFindAudience = async () => {
    setLoading(true);

    try {
      const response = await whatsappAudienceApi.estimate({
        q: filters.q || "",
        source: filters.source || null,
        status: filters.status || null,
        contactGroupId: filters.contact_group_id || null,
        limit: sampleSize,
        preferredLimit,
        excludeRecent,
        days: Number(recentDays || preset.defaultDays || 7),
        segment: preset.segment || filters.segment || "",
        audienceKind: audienceChoice === "my_contacts" ? "my_contacts" : null,
        includeMyContacts: audienceChoice === "all_eligible" ? true : null,
        targetAudience: filters.target_audience || "all",
      });

      const summary = {
        totalFound: Number(response.total_found || 0),
        eligibleFound: Number(response.eligible_found ?? 0),
        suppressedFound: Number(response.suppressed_found ?? 0),
        estimatedCost: Number(response.estimated_cost ?? 0),
        remainingAfterEstimate:
          response.remaining_after_estimate === null || response.remaining_after_estimate === undefined
            ? null
            : Number(response.remaining_after_estimate),
        costPerRecipient: Number(response.cost_per_recipient ?? 0),
        budget: response.budget || null,
        suppressedContacts: Array.isArray(response.suppressed_contacts) ? response.suppressed_contacts : [],
        suppressedBreakdown:
          response.suppressed_breakdown && typeof response.suppressed_breakdown === "object"
            ? response.suppressed_breakdown
            : {},
        sampleSize: Number(response.sample_size || sampleSize),
        sampleReturned: Number(response.sample_returned || 0),
        preferredLimit: response.preferred_limit ? Number(response.preferred_limit) : null,
      };

      onAudiencePrepared?.({
        summary,
        previewRows: (response.contacts || []).map(toAudienceRow),
        plan: buildAudiencePlan({
          audienceKey: audienceChoice,
          filters: {
            ...filters,
            segment: preset.segment || filters.segment || "",
          },
          excludeRecent,
          recentDays,
        }),
      });
      setRequestError("");
    } catch (error) {
      clearPreparedAudience();
      setRequestError(
        error?.response?.data?.message
          || error?.message
          || "We could not find this audience right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSharedCap = async () => {
    const dailyLimit = parseBudgetInputValue(budgetForm.daily_limit);
    const monthlyLimit = parseBudgetInputValue(budgetForm.monthly_limit);
    const reserveBuffer = parseBudgetInputValue(budgetForm.reserve_buffer, 0);

    if (Number.isNaN(dailyLimit) || Number.isNaN(monthlyLimit) || Number.isNaN(reserveBuffer)) {
      setBudgetError("Shared cap values must be valid numbers.");
      return;
    }

    setBudgetSaving(true);
    setBudgetError("");
    setBudgetNotice("");

    try {
      const response = await api.put("/analytics/marketing/broadcast-budget/whatsapp", {
        daily_limit: dailyLimit,
        monthly_limit: monthlyLimit,
        reserve_buffer: reserveBuffer ?? 0,
      });

      const channel = response?.data?.data?.channels?.whatsapp || null;
      onWhatsAppBudgetUpdated?.(channel);
      clearPreparedAudience();
      setBudgetEditorOpen(false);
      setBudgetNotice("Shared WhatsApp cap updated. Run Find Audience again to refresh the sendable count.");
    } catch (error) {
      setBudgetError(
        error?.response?.data?.message
          || error?.message
          || "We could not save the shared WhatsApp cap right now."
      );
    } finally {
      setBudgetSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {loading ? <LinearProgress sx={{ mb: 2, borderRadius: 1 }} /> : null}

      <Stack spacing={0.75} sx={{ mb: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={900}>
          Who do you want to message?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose an audience outcome first. We will estimate how many contacts can receive this WhatsApp broadcast before the message is scheduled.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap">
        <Button
          variant={audienceMode === "simple" ? "contained" : "outlined"}
          onClick={() => onAudienceModeChange("simple")}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Simple Mode
        </Button>
        <Button
          variant={audienceMode === "advanced" ? "contained" : "outlined"}
          onClick={() => {
            onAudienceModeChange("advanced");
            setAdvancedOpen(true);
          }}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Advanced Mode
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {AUDIENCE_PRESETS.map((item) => {
          const active = audienceChoice === item.key;
          return (
            <Grid item xs={12} sm={6} md={4} key={item.key}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: active ? "#10B981" : "#E5E7EB",
                  bgcolor: active ? "#F0FDF4" : "white",
                }}
              >
                <CardActionArea sx={{ height: "100%" }} onClick={() => handlePresetSelect(item.key)}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 999,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: active ? "rgba(16,185,129,0.16)" : "#F3F4F6",
                          color: active ? "#047857" : "#374151",
                          flexShrink: 0,
                        }}
                      >
                        {PRESET_ICONS[item.key]}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={900}>
                            {item.label}
                          </Typography>
                          {active ? <Chip size="small" color="success" label="Selected" /> : null}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* AI Intelligence audience picker */}
      {audienceChoice === "ai_intelligence" && (
        <Box sx={{ mb: 2.5, p: 2, borderRadius: 3, border: "1px solid #7C3AED", bgcolor: "#FAF5FF" }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <AutoAwesomeIcon sx={{ color: "#7C3AED", fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={900} color="#5B21B6">
              Pick an approved Intelligence segment
            </Typography>
          </Stack>
          {approvedAudiences.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No approved audience segments yet. Go to the Intelligence Hub weekly plan and approve one first.
            </Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel shrink>Approved audience</InputLabel>
              <Select
                label="Approved audience"
                displayEmpty
                value={selectedIntelligenceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedIntelligenceId(id);
                  clearPreparedAudience();
                  if (id) applyIntelligenceAudience(id);
                }}
                renderValue={(val) => {
                  if (!val) return "Choose a segment…";
                  const a = approvedAudiences.find((x) => String(x.id) === String(val));
                  return a ? `${a.audience_name} (${a.estimated_size} contacts)` : val;
                }}
              >
                <MenuItem value="">Choose a segment…</MenuItem>
                {approvedAudiences.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    <Stack>
                      <Typography variant="body2" fontWeight={700}>{a.audience_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.estimated_size} contacts · {a.confidence_score}% confidence · Week of {a.week_of}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={900}>
          {audienceMode === "advanced" || manualSelection ? "Advanced filters" : "Refine this audience"}
        </Typography>
        <Button
          onClick={() => setAdvancedOpen((current) => !current)}
          endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {advancedOpen ? "Hide filters" : "Advanced filters"}
        </Button>
      </Stack>

      <Collapse in={advancedOpen || audienceMode === "advanced" || manualSelection}>
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FAFAFA" }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Search by name, phone, or email"
                value={filters.q}
                onChange={(event) => {
                  clearPreparedAudience();
                  onFiltersChange({ ...filters, q: event.target.value });
                }}
                placeholder="Use this when you want to narrow the audience"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{ minWidth: { xs: "100%", md: 220 } }}>
                <InputLabel id="whatsapp-audience-source-label" shrink>
                  Include source
                </InputLabel>
                <Select
                  labelId="whatsapp-audience-source-label"
                  label="Include source"
                  displayEmpty
                  value={filters.source || ""}
                  onChange={(event) => {
                    clearPreparedAudience();
                    onFiltersChange({ ...filters, source: event.target.value });
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return "All Sources";
                    }

                    return {
                      inquiries: "Inquiries",
                      hubspot: "HubSpot",
                      google: "Google",
                      excel: "Excel",
                      manual: "Manual",
                    }[selected] || selected;
                  }}
                >
                  <MenuItem value="">All Sources</MenuItem>
                  <MenuItem value="inquiries">Inquiries</MenuItem>
                  <MenuItem value="hubspot">HubSpot</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="number"
                label="Sample size"
                value={filters.limit}
                onChange={(event) => {
                  clearPreparedAudience();
                  onFiltersChange({ ...filters, limit: Number(event.target.value || 200) });
                }}
                fullWidth
                helperText="Only used for review samples, not the final broadcast size."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="whatsapp-audience-status-label" shrink>
                  Lead status
                </InputLabel>
                <Select
                  labelId="whatsapp-audience-status-label"
                  label="Lead status"
                  displayEmpty
                  value={filters.status || ""}
                  onChange={(event) => {
                    clearPreparedAudience();
                    onFiltersChange({ ...filters, status: event.target.value });
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Any lead status";
                    }

                    return statusOptions.find((option) => String(option.value) === String(selected))?.label || selected;
                  }}
                >
                  <MenuItem value="">Any lead status</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="whatsapp-audience-group-label" shrink>
                  Contact database
                </InputLabel>
                <Select
                  labelId="whatsapp-audience-group-label"
                  label="Contact database"
                  displayEmpty
                  value={filters.contact_group_id || ""}
                  onChange={(event) => {
                    clearPreparedAudience();
                    onFiltersChange({ ...filters, contact_group_id: event.target.value });
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return "All databases";
                    }

                    const group = contactGroups.find((item) => String(item.id) === String(selected));
                    return group?.name || selected;
                  }}
                >
                  <MenuItem value="">All databases</MenuItem>
                  {contactGroups.map((group) => (
                    <MenuItem key={group.id} value={String(group.id)}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="whatsapp-audience-target-label">Who can receive it</InputLabel>
                <Select
                  labelId="whatsapp-audience-target-label"
                  label="Who can receive it"
                  value={filters.target_audience || "all"}
                  onChange={(event) => {
                    clearPreparedAudience();
                    onFiltersChange({ ...filters, target_audience: event.target.value });
                  }}
                >
                  {TARGET_AUDIENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Alert severity="info" sx={{ height: "100%", alignItems: "center" }}>
                Custom audience review includes the full contact picker, so you can still import lists or build a database in the next step.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 1.5, bgcolor: "white" }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={900}>
                      Delivery guardrails
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Keep the sender safe by excluding contacts messaged in the last {recentDays} days.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <TextField
                      label="Lookback days"
                      type="number"
                      size="small"
                      value={recentDays}
                      onChange={(event) => {
                        clearPreparedAudience();
                        setRecentDays(Number(event.target.value || preset.defaultDays || 7));
                      }}
                      sx={{ width: 140 }}
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={excludeRecent}
                          onChange={(event) => {
                            clearPreparedAudience();
                            setExcludeRecent(event.target.checked);
                          }}
                        />
                      )}
                      label="Exclude recently contacted"
                    />
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 1.5, bgcolor: "white" }}>
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} justifyContent="space-between" alignItems={{ md: "center" }}>
                    <Typography variant="subtitle2" fontWeight={900}>
                      Send cap
                    </Typography>
                    {canManageBudget ? (
                      <Button
                        size="small"
                        variant={budgetEditorOpen ? "contained" : "outlined"}
                        onClick={() => setBudgetEditorOpen((current) => !current)}
                        disabled={budgetLoading || budgetSaving}
                        sx={{ alignSelf: { xs: "flex-start", md: "center" }, textTransform: "none", fontWeight: 800 }}
                      >
                        {budgetEditorOpen ? "Hide shared cap" : "Adjust shared cap"}
                      </Button>
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {budgetLoading
                      ? "Loading shared WhatsApp budget..."
                      : budgetSendable === null
                        ? "No shared WhatsApp cap is configured right now. Add an optional preferred send limit below if you want this broadcast to stop early."
                        : `The shared WhatsApp budget can currently queue about ${budgetSendable.toLocaleString()} recipients${budgetReserve > 0 ? ` after holding back ${budgetReserve.toLocaleString()} in reserve` : ""}.`}
                  </Typography>
                  {budgetError ? <Alert severity="error">{budgetError}</Alert> : null}
                  {budgetNotice ? <Alert severity="success">{budgetNotice}</Alert> : null}
                  <Collapse in={budgetEditorOpen}>
                    <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 1.5, bgcolor: "#FAFAFA" }}>
                      <Stack spacing={1.25}>
                        <Typography variant="body2" fontWeight={800}>
                          Adjust the shared WhatsApp cap
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This is the global cap used by all WhatsApp broadcasts. Leave daily or monthly blank to remove that specific limit.
                        </Typography>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              type="number"
                              label="Daily cap"
                              value={budgetForm.daily_limit}
                              onChange={(event) => setBudgetForm((current) => ({ ...current, daily_limit: event.target.value }))}
                              fullWidth
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              type="number"
                              label="Monthly cap"
                              value={budgetForm.monthly_limit}
                              onChange={(event) => setBudgetForm((current) => ({ ...current, monthly_limit: event.target.value }))}
                              fullWidth
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              type="number"
                              label="Reserve buffer"
                              value={budgetForm.reserve_buffer}
                              onChange={(event) => setBudgetForm((current) => ({ ...current, reserve_buffer: event.target.value }))}
                              fullWidth
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                        </Grid>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setBudgetError("");
                              setBudgetNotice("");
                              setBudgetForm({
                                daily_limit: toBudgetInputValue(whatsAppBudget?.settings?.daily_limit ?? whatsAppBudget?.daily_limit),
                                monthly_limit: toBudgetInputValue(whatsAppBudget?.settings?.monthly_limit ?? whatsAppBudget?.monthly_limit),
                                reserve_buffer: toBudgetInputValue(whatsAppBudget?.settings?.reserve_buffer ?? whatsAppBudget?.reserve_buffer ?? 0),
                              });
                            }}
                            disabled={budgetSaving}
                            sx={{ textTransform: "none", fontWeight: 800 }}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveSharedCap}
                            disabled={budgetSaving}
                            sx={{ textTransform: "none", fontWeight: 800 }}
                          >
                            {budgetSaving ? "Saving..." : "Save shared cap"}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Collapse>
                  <TextField
                    type="number"
                    label="Preferred send limit"
                    value={filters.preferred_limit ?? ""}
                    onChange={(event) => {
                      clearPreparedAudience();
                      onFiltersChange({
                        ...filters,
                        preferred_limit: event.target.value === "" ? "" : Number(event.target.value),
                      });
                    }}
                    fullWidth
                    inputProps={{ min: 1, max: 50000 }}
                    helperText="Optional. When this limit is reached, the remaining contacts are held back and the operator gets a WhatsApp alert."
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid #E5E7EB",
          background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(255,255,255,0.98) 46%, rgba(59,130,246,0.08) 100%)",
        }}
      >
        <Stack spacing={1.5}>
          {requestError ? <Alert severity="error">{requestError}</Alert> : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={900}>
                Live audience summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {audienceSummary ? audienceNarrative : "Run Find Audience to see exactly how many contacts will receive this broadcast and how many will be skipped."}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                <Chip size="small" variant="outlined" label={`Outcome: ${preset.shortLabel}`} sx={{ fontWeight: 800 }} />
                <Chip size="small" variant="outlined" label={`Mode: ${audienceMode === "simple" ? "Simple" : "Advanced"}`} sx={{ fontWeight: 800 }} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Guardrails: ${excludeRecent ? `${recentDays}d lookback` : "Off"}`}
                  sx={{ fontWeight: 800 }}
                />
                {filters.target_audience && filters.target_audience !== "all" ? (
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={`Audience: ${targetAudienceLabel(filters.target_audience)}`}
                    sx={{ fontWeight: 800 }}
                  />
                ) : null}
                {preferredLimit ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Preferred limit: ${preferredLimit.toLocaleString()}`}
                    sx={{ fontWeight: 800 }}
                  />
                ) : null}
              </Stack>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.25, minWidth: { xs: "100%", md: 400 } }}>
              <MetricCard label="Matched" value={audienceBadges.matched} tone="success" />
              <MetricCard label="Will send" value={audienceBadges.sendable} tone="primary" />
              <MetricCard label="Will skip" value={audienceBadges.skipped} tone="warning" />
            </Box>
          </Stack>

          {sampleRows.length > 0 ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {previewRows?.length > sampleRows.length
                  ? `Sample recipients (showing ${sampleRows.length.toLocaleString()} of ${previewRows.length.toLocaleString()})`
                  : `Sample recipients (${(previewRows?.length || 0).toLocaleString()})`}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {sampleRows.map((row) => (
                  <Chip
                    key={row.id}
                    label={row.name ? `${row.name} <${row.phone}>` : row.phone}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          ) : null}

          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleFindAudience}
              disabled={loading}
              startIcon={<PlaylistAddCheckCircleIcon />}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              Find Audience
            </Button>

            <Button
              variant="outlined"
              color="success"
              onClick={onUseAudience}
              disabled={!canUseAudience}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              {manualSelection ? "Review & Pick Recipients" : "Use This Audience"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

function MetricCard({ label, value, tone }) {
  const palette = {
    success: { border: "#DCFCE7", bg: "#F0FDF4" },
    primary: { border: "#DBEAFE", bg: "#EFF6FF" },
    warning: { border: "#FEF3C7", bg: "#FFFBEB" },
  }[tone];

  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: palette.border, bgcolor: palette.bg }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.1 }}>
        {Number(value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}
